import { useState } from "react";

const DEFAULT_STEP = 50;

/**
 * Client-side "show more on scroll" virtualization: keeps all data in memory
 * but only renders an incrementally growing slice. Pairs with an infinite
 * scroll trigger that calls `loadMore`. The visible count resets whenever the
 * dataset size changes (e.g. filters change), but not when it is merely
 * reordered, so sorting keeps the current scroll depth.
 */
export const useLoadMore = <T>(data: T[], step: number = DEFAULT_STEP) => {
  const [visibleCount, setVisibleCount] = useState(step);
  const [prevLength, setPrevLength] = useState(data.length);

  if (prevLength !== data.length) {
    setPrevLength(data.length);
    setVisibleCount(step);
  }

  const visibleData = data.slice(0, visibleCount);
  const hasMore = visibleCount < data.length;
  const loadMore = () =>
    setVisibleCount((count) => Math.min(count + step, data.length));

  return { visibleData, hasMore, loadMore };
};
